import { useState, useMemo, useCallback } from "react";
import { Transaction } from "@/context/FinanceContext";
import { format, parseISO, isToday } from "date-fns";

/**
 * Custom hook for managing transaction filters
 * @description Handles all filter state and logic for transaction lists
 * @returns Filter state and handlers
 */
export function useTransactionFilters() {
  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>({});

  /**
   * Updates filters for a specific column
   */
  const handleColumnFilterChange = useCallback((column: string, values: string[]) => {
    setColumnFilters(prev => ({
      ...prev,
      [column]: values
    }));
  }, []);

  /**
   * Clears all active filters
   */
  const handleClearAllFilters = useCallback(() => {
    setColumnFilters({});
  }, []);

  /**
   * Removes a specific value from a column filter
   */
  const handleRemoveColumnFilter = useCallback((column: string, value: string) => {
    setColumnFilters(prev => {
      const currentValues = prev[column] || [];
      const newValues = currentValues.filter(v => v !== value);
      if (newValues.length === 0) {
        const newFilters = { ...prev };
        delete newFilters[column];
        return newFilters;
      }
      return {
        ...prev,
        [column]: newValues
      };
    });
  }, []);

  /**
   * Updates date range filters
   */
  const handleDateRangeChange = useCallback((dateType: string, startDate?: string, endDate?: string) => {
    if (!startDate && !endDate) {
      setColumnFilters(prev => {
        const updated = { ...prev };
        delete updated[dateType];
        // Se limpou filtro de Pagamento e o status estava forçado para Pago, remover
        if (dateType === 'paymentDate' && updated.status?.length === 1 && updated.status[0] === 'Pago') {
          delete updated.status;
        }
        return updated;
      });
      return;
    }

    const rangeFilter = {
      type: 'dateRange',
      start: startDate,
      end: endDate
    };

    setColumnFilters(prev => {
      const next = {
        ...prev,
        [dateType]: [JSON.stringify(rangeFilter)],
      };
      // Ao filtrar por data de Pagamento, só faz sentido exibir lançamentos com status "Pago"
      if (dateType === 'paymentDate') {
        next.status = ['Pago'];
      }
      return next;
    });
  }, []);

  /**
   * Filters transactions based on transactionIds (for URL parameters)
   * Handles special 'empty' marker to show zero results
   */
  const filterByTransactionIds = useCallback((
    transactions: Transaction[],
    transactionIds?: string[]
  ): Transaction[] => {
    if (transactionIds && transactionIds.length > 0) {
      // Special marker for zero results
      if (transactionIds.length === 1 && transactionIds[0] === 'empty') {
        return [];
      }
      return transactions.filter(t => transactionIds.includes(t.id));
    }
    return transactions;
  }, []);

  /**
   * Checks if a date falls within a date range
   */
  const isDateInRange = useCallback((dateString: string | undefined, rangeFilter: any): boolean => {
    if (!dateString) return false;
    
    try {
      const parsedFilter = typeof rangeFilter === 'string' 
        ? JSON.parse(rangeFilter) 
        : rangeFilter;
      
      if (parsedFilter.type !== 'dateRange') return false;
      
      const date = parseISO(dateString);
      const start = parsedFilter.start ? parseISO(parsedFilter.start) : null;
      const end = parsedFilter.end ? parseISO(parsedFilter.end) : null;
      
      if (start && date < start) return false;
      if (end && date > end) return false;
      
      return true;
    } catch {
      return false;
    }
  }, []);

  return {
    columnFilters,
    setColumnFilters,
    handleColumnFilterChange,
    handleClearAllFilters,
    handleRemoveColumnFilter,
    handleDateRangeChange,
    filterByTransactionIds,
    isDateInRange
  };
}
